import {
  FluidAnchorPosition,
  FluidColor,
  FluidCustomStyles,
  FluidIconConfig,
  FluidIconKey,
  FluidPopoverConfig,
  FluidPopoverTranslationOptions,
  FluidPosition,
  FluidSize,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  NO_ICON_BY_KEY,
  STANDALONE_ICON_ONLY,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { loadIcon } from '../../utils/icons/icon-utils';
import { validatePopoverProp } from '../../utils/logging/deprecation.utils';
import { Utils } from '../../utils/utils';
import {
  BaseIcon,
  FluidIconRef,
  FluidIconStyleProps,
  getPopoverProps,
  IconSlot,
  IconWithPopover,
} from './components/fluid-icon.components';
import {
  getIconAriaProps,
  getIconContainerClass,
  getIconContainerStyle,
  getIconPosition,
  getIconStyle,
} from './fluid-icon-style.utils';

/**
 * @displayName Icon
 *
 * @slot - Unnamed slot for custom external icon (only available if "iconKey" NOT provided)
 * @slot CUSTOM_SLOT_NAME - Custom named slot generated from "slotName" property (only available if "iconKey" NOT provided)
 */
@Component({
  tag: 'fluid-icon',
  shadow: true,
  styleUrl: 'fluid-icon.less',
})
export class FluidIcon implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  _id: string;

  private _popoverConfig: FluidPopoverConfig;

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When provided, Icon will render an embedded SVG from FLUID's
   * icon collection. NOTE: when this Prop is NOT provided, Icon
   * will render an unnamed slot for custom icons (e.g. SVGs, images,
   * Font Awesome icons, etc.).
   */
  @Prop({ mutable: true }) iconKey: FluidIconKey;

  /**
   * Property to set the aria-label for accessibility compliance.
   * Note: if the Icon is used as an "indicator" (i.e. it's used by itself), then arialabel must be provided for accessibility.
   * However, if the Icon is used with another element that has a label OR is used within another component with a label, then arialabel should NOT be provided (i.e. the other element / component provides the necessary context)
   */
  @Prop({ mutable: true }) arialabel: string;

  /**
   * When this Prop is provided AND "iconKey" is NOT provided, Icon
   * will render a named slot instead of an embedded SVG. Slotted
   * children (e.g. custom SVGs, images, Font Awesome icons, etc.) will
   * need to have the following attribute: slot="slotName" ("slotName"
   * should be the value of this Prop). NOTE: this Prop should be used
   * when providing custom slotted icons for menu items within Context
   * Menu or Tree Menu.
   */
  @Prop({ mutable: true }) slotName: string;

  /**
   * Limiting icon size to a predefined range to ensure consistency
   */
  @Prop({ mutable: true }) size: FluidSize = FluidSize.SMALL;

  /**
   * Sets "fill" color of Icon's embedded SVG. If this Prop is NOT provided,
   * Icon will inherit the text color of its parent element by default.
   */
  @Prop({ mutable: true }) color: FluidColor;

  /**
   * The icon will infinitely rotate while this value is true
   */
  @Prop({ mutable: true }) rotate: boolean;

  /**
   * Flip the icon vertically, with animation, when this value is true
   */
  @Prop({ mutable: true }) flipVertical: boolean;

  /**
   * Inlines an icon to the text beside it.
   */
  @Prop({ mutable: true }) inline: boolean;

  /**
   * Custom CSS styles to apply to icon container
   */
  @Prop({ mutable: true }) customStyles: FluidCustomStyles;

  /**
   * If the icon position inside its container needs tweaked, use pixel values for
   * top, bottom, left and right.
   */
  @Prop({ mutable: true }) position: FluidPosition;

  /**
   * If we need to override the scaling down of an icon, use this prop. Scales
   * are in multiples of 5 and correspond to a percentage reduction. If you find you
   * need to scale up, use the next size up and scale down accordingly.
   */
  @Prop({ mutable: true }) scale: number;

  /**
   * When provided, adds a Popover to Icon that is displayed on :hover.
   * Accepts either a string or FluidPopoverConfig value - string value
   * generates a simple Popover, while a FluidPopoverConfig value allows
   * all Popover Props to be customized.
   */
  @Prop({ mutable: true }) popoverConfig: string | FluidPopoverConfig;

  @Watch('popoverConfig')
  onPopoverConfigChange() {
    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      undefined
    );
  }

  /**
   * If the icon should be disabled for click/pointer events.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * If the icon does not have a popover or isn't a clickable icon, then cursor should be disabled.
   * Enable this prop for clickable icons.
   */
  @Prop({ mutable: true }) interactive = false;

  /**
   * Single configuration object that populates all local Props.
   */
  @Prop({ mutable: true }) config: FluidIconConfig;

  @Watch('config')
  onConfigChange() {
    this.mapConfigToLocalProps();
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: mapConfigToLocalProps - set local @Props to "config" values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: validatePopoverProp - map deprecated "popover" Prop to "_popoverConfig"
   */
  connectedCallback(): void {
    this.mapConfigToLocalProps(); // Run before setGlobalTheme() to allow config to set theme
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({ init: () => (this._i18nBound = true) });
    this._id = this.host.id;

    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      this.popover
    );
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.svgStylePolyfill();
  }

  // ================================================================ //
  // -- Local Methods

  mapConfigToLocalProps() {
    if (!this.config) return;
    const { key, ...config } = this.config;
    Utils.selectiveMapConfigToLocal(this, {
      ...config,
      ...(!!key && { iconKey: key }),
    });
  }

  /**
   * Applies inline styles to embedded SVG when Shadow DOM not supported.
   * NOTE: When Shadow DOM isn't supported, StencilJS applies "scoped"
   * styles to elements that are explicitly defined within a component's
   * "render()" function as an alternative to Shadow DOM. However,
   * StencilJS does NOT apply "scoped" styles to elements that are
   * injected via "innerHTML". This causes a problem for FLUID Icon's
   * embedded SVG - since it's injected via "innerHTML", it doesn't
   * receive the styles defined in the component's LESS file when "scoped"
   * styles are applied, which can break its appearance in certain
   * situations. This method compensates for this problem by applying
   * the SVGs CSS directly to the element as inline styles.
   */
  svgStylePolyfill(): void {
    if (this.host.shadowRoot) return;
    const embeddedSvg = Utils.querySelector(this.host, 'svg') as HTMLElement;
    const svgStyles = {
      width: 'max-content',
      height: 'max-content',
      maxWidth: '100%',
      maxHeight: '100%',
    };

    if (!embeddedSvg) return;
    for (const property in svgStyles) {
      embeddedSvg.style[property] = svgStyles[property];
    }
  }

  // ================================================================ //
  // -- Render (Partials)

  /**
   * Renders an <i> tag with inner html of the embedded svg code if
   * iconKey prop is provided.
   */
  renderEmbeddedIcon(): HTMLElement {
    const iconRef: FluidIconRef = loadIcon(this.iconKey, this.theme);

    const iconProps: FluidIconStyleProps = {
      iconStyle: getIconStyle.bind(this)(),
      containerStyle: getIconContainerStyle.bind(this)(),
      containerClass: getIconContainerClass.bind(this)(iconRef),
      iconPosition: getIconPosition.bind(this)(),
      ariaProps: getIconAriaProps.bind(this)(),
    };

    if (!iconRef) {
      Utils.consoleWarn(this.host, NO_ICON_BY_KEY(this.iconKey));
    }

    if (iconRef?.standalone) {
      Utils.consoleWarn(this.host, STANDALONE_ICON_ONLY(this.iconKey), true);
    }
    return (
      <BaseIcon
        iconRef={iconRef}
        includeAdornment={this.includeAdornment}
        iconSize={this.size}
        {...iconProps}
      />
    );
  }

  renderIcon(childOfPopover?: boolean) {
    switch (true) {
      case !childOfPopover && !!this._popoverConfig?.message:
        return (
          <IconWithPopover
            popoverConfig={getPopoverProps.bind(this)()}
            renderIcon={() => this.renderIcon(true)}
            fluidTranslate={this.fluidTranslate}
          />
        );
      case !!this.iconKey:
        return this.renderEmbeddedIcon();
      case !!this.slotName:
        return <IconSlot slotName={this.slotName} />;
      default:
        return <slot />;
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'fluid-icon-size-full': this.size === FluidSize.FULL,
        }}>
        {this.renderIcon()}
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * If "popover" Prop is specified, provides Popover's parent element
   * to be used for edge detection.
   * @internal
   */
  @Prop({ mutable: true }) popoverParentContainer: HTMLElement;

  /**
   * If Icon has Popover, sets "ignoreClippingBoundaries" Prop value.
   * @internal
   */
  @Prop({ mutable: true }) popoverIgnoreClippingBoundaries = true;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "popover" is now deprecated (https://chromestatus.com/feature/5463833265045504).
   * Instead, use the "message" property of the "popoverConfig" Prop. --
   * If provided, adds simple "tooltip" Popover to Icon that is
   * displayed on hover. NOTE: if you need to customize Popover's
   * Props, use the "popoverConfig" Prop instead to access ALL of
   * Popover's Props.
   * @deprecated
   */
  @Prop({ mutable: true }) popover: string;

  @Watch('popover')
  onPopoverChange() {
    if (this.popover === null) return;
    this._popoverConfig = validatePopoverProp(
      this.host,
      undefined,
      this.popover
    );
  }

  /**
   * DEPRECATED: "popoverPersistable" is now deprecated. Instead, use
   * the "persistable" property of the "popoverConfig" Prop. --
   * When false, the Icon's Popover will NOT remain open when clicked.
   * @deprecated
   */
  @Prop({ mutable: true }) popoverPersistable = true;

  /**
   * DEPRECATED: "popoverPlacement" is now deprecated. Instead, use
   * the "placement" property of the "popoverConfig" Prop. --
   * If Popover exists, which side of the Icon should display the Popover?
   * @deprecated
   */
  @Prop({ mutable: true }) popoverPlacement: FluidAnchorPosition;

  /**
   * DEPRECATED: "popoverWidth" is now deprecated. Instead, use
   * the "width" property of the "popoverConfig" Prop. --
   * If Popover exists, explicitly sets the Popover's maximum width.
   * @deprecated
   */
  @Prop({ mutable: true }) popoverWidth: number;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: {
    arialabel?: string;
    popoverConfig: FluidPopoverTranslationOptions;
  };
  private _translateId: string = generateUniqueId();
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;

  @Prop({ mutable: true }) includeAdornment = false;
}
